import { truncate } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import injectSheet from 'react-jss';

import EcCard from '~/components/Shared/EcCard/EcCard';
import { Button } from '~/eds/src/components/Button/Button';
import { Table } from '~/eds/src/modules/Table/Table';
import { useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { MODAL_SIMPLE } from '~/types/modal.types';
import Box from '~/ui/components/Box/Box';
import Text from '~/ui/components/Text/Text';

import EcModal from '../../../Shared/EcModal/EcModal';
import styles from './MigratedProvisions.styles';

const _MigratedProvisions = (props) => {
  const [clause, setClause] = useState({});
  const [page, setPage] = useState(1);
  const { documentId, classes } = props;
  const intl = useIntl();

  const { data, isFetching } = api.endpoints.getMigratedClauses.useQuery({
    docId: documentId,
    page,
  });

  const columns = [
    {
      key: 'name',
      title: intl.formatMessage({
        id: 'MigratedProvisions.clauseTitleColumn',
        defaultMessage: 'Clause Title',
        description: 'Column header for clause title',
      }),
      renderCell: (cell) => {
        return <Text className={classes.clauseTitle}>{cell.name}</Text>;
      },
    },
    {
      key: 'content',
      title: intl.formatMessage({
        id: 'MigratedProvisions.clauseTextColumn',
        defaultMessage: 'Clause Text',
        description: 'Column header for clause text content',
      }),
      width: 'auto',
      disableResizing: true,
      renderCell: (cell) => {
        return (
          <Box className={classes.clauseText}>
            {/* TODO: pull truncate into a global utility function */}
            <Text variant="body1">
              {truncate(cell.content, { length: 500, omission: '...' })}
            </Text>
            {cell.content.length > 500 ? (
              <Button
                onClick={() => openClause(cell)}
                variant="action"
                text={intl.formatMessage({
                  id: 'MigratedProvisions.readMoreButton',
                  defaultMessage: 'Read More',
                  description: 'Button text to expand full clause content',
                })}
              />
            ) : null}
          </Box>
        );
      },
    },
  ];

  const openClause = (c) => {
    setClause(c);
  };
  const clearClause = () => {
    setClause({});
  };

  const renderClauseModal = () => {
    return clause.content ? (
      <EcModal
        width="64vw"
        verticalCenter
        modalType={MODAL_SIMPLE}
        title={clause.name}
        hideModal={clearClause}
        modalButtons={
          <Button
            variant="tertiary"
            text={intl.formatMessage({
              id: 'MigratedProvisions.closeButton',
              defaultMessage: 'Close',
              description: 'Button text to close the clause modal',
            })}
            onClick={clearClause}
          />
        }
      >
        <div className={classes.clauseTextContainer}>
          <Text variant="body1" className={classes.clauseText}>
            {clause.content}
          </Text>
        </div>
      </EcModal>
    ) : null;
  };

  return data?.results.length ? (
    <EcCard
      title={intl.formatMessage({
        id: 'MigratedClauses.cardTitle',
        defaultMessage: 'Migrated Clauses',
        description: 'Title for the migrated clauses card section',
      })}
      contentStyles={{ display: 'flex', padding: '16px', marginBottom: '24px' }}
    >
      <Table
        name={intl.formatMessage({
          id: 'MigratedClauses.tableName',
          defaultMessage: 'Migrated Clauses',
          description: 'Accessible name for the migrated clauses table',
        })}
        data={data?.results}
        columns={columns}
        state={{ pageIndex: page, pageSize: 10 }}
        options={{
          enableExportXlsx: false,
          enableSelectRows: false,
          enableManageColumns: false,
        }}
        totalCount={data.count}
        isLoading={isFetching}
        onPaginate={({ pageIndex }) => {
          setPage(pageIndex);
        }}
      />
      {renderClauseModal()}
    </EcCard>
  ) : null;
};

const MigratedProvisions = React.memo(injectSheet(styles)(_MigratedProvisions));

MigratedProvisions.propTypes = {
  documentId: PropTypes.number.isRequired,
};

export default MigratedProvisions;
